import { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Grid,
  Icon,
  IconButton,
  Input,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useParams } from 'react-router-dom';
import { Field } from '@/components/ui/field';
import { SimpleSelect } from '@/components/ui/simple-select';
import { SortableList } from '@/components/sortable-list';
import { slotFor } from '@/lib/slot-color';
import { CourseItemDraft, uid } from '@/pages/courses/api';
import ContentBlock from '@/components/fillable-blocks/section-blocks/content-block';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import useContentTypesStore from '@/utils/use-content-type';
import { Content, ContentType } from '@/lib/types';
import { contentTypeShortcut, builderLabel } from '@/pages/courses/shortcuts';

// SimpleSelect reads '' as "nothing selected", so "None" rides on a sentinel
// that is mapped back to '' before it reaches the module.
const NO_SHORTCUT = 'none';

// Fully-snapshotted item draft from a library pick — copies what we need at
// add time so later renames of the source content don't retroactively change
// the course (same rationale as planner's itemFromContent).
function itemFromContent(content: Content, workspaceId: number): CourseItemDraft {
  return {
    uid: uid(),
    content_id: content.id,
    content_reference_id: content.reference_id,
    content_type_id: content.content_type_id,
    content_type_name: content.content_type_name ?? '',
    content_name: content.name,
    source_label:
      content.workspace_id === workspaceId
        ? 'My workspace'
        : content.workspace_name || content.workspace || 'Partner library',
  };
}

export interface ModuleDraft {
  id: number;
  name: string;
  instructions: string;
  assignment_text: string;
  assignment_shortcut: string;
  items: CourseItemDraft[];
}

interface ModuleCardProps {
  module: ModuleDraft;
  index: number;
  // Field edits persist on blur; item changes persist immediately.
  onFieldsBlur: (module: ModuleDraft) => void;
  onFieldsChange: (module: ModuleDraft) => void;
  onItemsChange: (module: ModuleDraft, items: CourseItemDraft[]) => void;
  onRemove: (module: ModuleDraft) => void;
}

// One module in the builder's content step: header row (drag handle, number,
// remove), name + instructions, the item list with library search, and the
// optional assignment block. Mirrors the prototype's module card.
export default function ModuleCard({
  module,
  index,
  onFieldsBlur,
  onFieldsChange,
  onItemsChange,
  onRemove,
}: ModuleCardProps) {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();
  const { contentTypes } = useContentTypesStore();
  const [pickerType, setPickerType] = useState<ContentType>();
  const picker = useModalToggle();
  // Assignment shortcut targets: one builder per content library in this
  // workspace, plus the plan builder. The value is stored on the module and
  // resolved to a deep link in the learner player.
  const shortcuts = useMemo(() => {
    const options = [
      { value: NO_SHORTCUT, label: 'None — written instructions only' },
      ...contentTypes.map((type) => ({
        value: contentTypeShortcut(type.id),
        label: builderLabel(type.name),
      })),
      { value: 'planner', label: 'Plan builder' },
    ];
    // Keep a saved shortcut visible even if its library is gone or still loading.
    if (
      module.assignment_shortcut &&
      !options.some((option) => option.value === module.assignment_shortcut)
    ) {
      options.push({ value: module.assignment_shortcut, label: 'Unavailable builder' });
    }
    return options;
  }, [contentTypes, module.assignment_shortcut]);
  // Local UI toggle so opening the assignment editor doesn't have to write a
  // placeholder into the draft; a module with saved text opens expanded.
  const [assignmentOpen, setAssignmentOpen] = useState(module.assignment_text !== '');

  const patch = (changes: Partial<ModuleDraft>) => {
    onFieldsChange({ ...module, ...changes });
  };

  const divider = (label: string) => (
    <Flex align='center' gap={3}>
      <Box h='1px' bg='border' flex='1' />
      <Text
        fontFamily='heading'
        fontSize='2xs'
        fontWeight='700'
        textTransform='uppercase'
        letterSpacing='0.16em'
        color='fg.muted'
      >
        {label}
      </Text>
      <Box h='1px' bg='border' flex='1' />
    </Flex>
  );

  return (
    <Box
      bg='bg.panel'
      borderWidth='1px'
      borderColor='border'
      borderRadius='lg'
      shadow='sm'
      overflow='hidden'
    >
      {/* Header — the whole strip is the drag handle */}
      <Flex
        align='center'
        gap={2.5}
        px={5}
        py={3.5}
        borderBottomWidth='1px'
        borderColor='border'
      >
        <SortableList.DragHandle />
        <Center
          w='26px'
          h='26px'
          borderRadius='full'
          bg='brand.soft'
          fontFamily='heading'
          fontSize='xs'
          fontWeight='700'
          color='brand.fg'
          flexShrink={0}
        >
          {index + 1}
        </Center>
        <Text
          fontFamily='heading'
          fontSize='2xs'
          fontWeight='700'
          textTransform='uppercase'
          letterSpacing='0.16em'
          color='fg.muted'
        >
          Module {index + 1}
        </Text>
        <Text ml='auto' fontSize='xs' color='fg.muted'>
          {module.items.length} {module.items.length === 1 ? 'item' : 'items'}
        </Text>
        <IconButton
          aria-label='Remove module'
          variant='ghost'
          size='xs'
          color='fg.subtle'
          _hover={{ color: 'danger' }}
          onClick={() => onRemove(module)}
        >
          <Icon as={XMarkIcon} w={4} h={4} />
        </IconButton>
      </Flex>

      <Stack gap={4} p={5}>
        <Field label='Module name'>
          <Input
            value={module.name}
            onChange={(e) => patch({ name: e.target.value })}
            onBlur={() => onFieldsBlur(module)}
            maxLength={200}
          />
        </Field>

        <Field
          label='How to complete this module'
          helperText='Shown to the learner above the content, before they mark it complete.'
        >
          <Textarea
            rows={3}
            value={module.instructions}
            onChange={(e) => patch({ instructions: e.target.value })}
            onBlur={() => onFieldsBlur(module)}
          />
        </Field>

        {divider('Add content')}

        {/* One picker per library, as in the prototype: each opens the shared
            library drawer (workspace/partner tabs, search, field filters). */}
        {contentTypes.length > 0 ? (
          <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }} gap={3}>
            {contentTypes.map((type) => (
              <Button
                key={type.id}
                variant='outline'
                size='sm'
                justifyContent='flex-start'
                title={`Add ${type.name}`}
                onClick={() => {
                  setPickerType(type);
                  picker.open();
                }}
              >
                <Icon as={PlusIcon} w={4} h={4} color='brand.fg' />
                <Text as='span' truncate>
                  {type.name}
                </Text>
              </Button>
            ))}
          </Grid>
        ) : (
          <Text fontSize='sm' color='fg.muted'>
            This workspace has no content libraries yet.
          </Text>
        )}

        {module.items.length > 0 && (
          <SortableList
            // Wrapped so the drag identity (uid) never collides with the
            // server row id the draft also carries.
            items={module.items.map((item) => ({ id: item.uid, draft: item }))}
            onChange={(reordered) =>
              onItemsChange(
                module,
                reordered.map((wrapped) => wrapped.draft),
              )
            }
            renderItem={({ id, draft }) => {
              const slot = slotFor(draft.content_type_name);
              return (
                <SortableList.Item id={id}>
                  <Flex
                    align='center'
                    gap={3}
                    px={3}
                    py={2.5}
                    borderWidth='1px'
                    borderColor='border'
                    borderRadius='md'
                    bg='bg.panel'
                    flex='1'
                  >
                    <SortableList.DragHandle />
                    <Box minW={0} flex='1'>
                      <Text fontSize='sm' fontWeight='600' color='fg.strong' truncate>
                        {draft.content_name}
                      </Text>
                      {draft.source_label && (
                        <Text fontSize='xs' color='fg.muted'>
                          {draft.source_label}
                        </Text>
                      )}
                    </Box>
                    {draft.content_type_name && (
                      <Badge
                        bg={`slot${slot}.bg`}
                        color={`slot${slot}.fg`}
                        fontSize='2xs'
                        textTransform='uppercase'
                      >
                        {draft.content_type_name}
                      </Badge>
                    )}
                    <IconButton
                      aria-label={`Remove ${draft.content_name}`}
                      variant='ghost'
                      size='xs'
                      color='fg.subtle'
                      _hover={{ color: 'danger' }}
                      onClick={() =>
                        onItemsChange(
                          module,
                          module.items.filter((existing) => existing.uid !== draft.uid),
                        )
                      }
                    >
                      <Icon as={XMarkIcon} w={3.5} h={3.5} />
                    </IconButton>
                  </Flex>
                </SortableList.Item>
              );
            }}
          />
        )}

        {workspaceId && pickerType && (
          <ContentBlock
            {...picker.props}
            workspaceId={Number(workspaceId)}
            contentType={pickerType}
            contentRefs={module.items
              .map((item) => item.content_reference_id)
              .filter((ref): ref is string => !!ref)}
            showPartner
            onSuccess={(refs, picked) => {
              if (picked) {
                onItemsChange(module, [
                  ...module.items,
                  itemFromContent(picked, Number(workspaceId)),
                ]);
              } else {
                // A removal from the drawer: drop items whose ref it no longer lists.
                onItemsChange(
                  module,
                  module.items.filter(
                    (item) =>
                      !item.content_reference_id || refs.includes(item.content_reference_id),
                  ),
                );
              }
            }}
          />
        )}

        {module.items.length === 0 && (
          <Center
            borderWidth='1px'
            borderStyle='dashed'
            borderColor='border.emphasized'
            borderRadius='md'
            py={5}
          >
            <Text fontSize='sm' color='fg.muted'>
              No content in this module yet — pick a library above to add some.
            </Text>
          </Center>
        )}

        {divider('Assignment')}

        {assignmentOpen ? (
          <Box bg='bg.subtle' borderWidth='1px' borderColor='border' borderRadius='md' p={4}>
            <Flex align='center' justify='space-between' gap={3} mb={3}>
              <Text fontSize='sm' color='fg.muted'>
                The learner confirms this before the next module unlocks.
              </Text>
              <IconButton
                aria-label='Remove assignment'
                variant='ghost'
                size='xs'
                color='fg.subtle'
                _hover={{ color: 'danger' }}
                onClick={() => {
                  setAssignmentOpen(false);
                  const cleared = { ...module, assignment_text: '', assignment_shortcut: '' };
                  onFieldsChange(cleared);
                  onFieldsBlur(cleared);
                }}
              >
                <Icon as={XMarkIcon} w={4} h={4} />
              </IconButton>
            </Flex>
            <Stack gap={3}>
              <Field label='What the learner should do'>
                <Textarea
                  rows={2}
                  value={module.assignment_text}
                  onChange={(e) => patch({ assignment_text: e.target.value })}
                  onBlur={() => onFieldsBlur(module)}
                />
              </Field>
              <Field label='Shortcut' helperText='Drops the learner straight into that builder.'>
                <SimpleSelect
                  items={shortcuts}
                  value={module.assignment_shortcut || NO_SHORTCUT}
                  onValueChange={(value) => {
                    const shortcut = value === NO_SHORTCUT ? '' : value;
                    const changed = { ...module, assignment_shortcut: shortcut };
                    onFieldsChange(changed);
                    onFieldsBlur(changed);
                  }}
                />
              </Field>
            </Stack>
          </Box>
        ) : (
          <Button
            variant='ghost'
            size='sm'
            borderWidth='1px'
            borderStyle='dashed'
            borderColor='border.emphasized'
            color='fg.muted'
            onClick={() => setAssignmentOpen(true)}
          >
            <Icon as={PlusIcon} w={4} h={4} color='brand.fg' />
            Add an assignment the learner does before the next module
          </Button>
        )}
      </Stack>
    </Box>
  );
}
